/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-plusplus */
/* eslint-disable arrow-body-style */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';
import Center from '../../assets/styles/Center';
import DefaultContainer from '../../assets/styles/DefaultContainer';
import { Error } from '../../components/Ad/styles';
import Loader from '../../components/Loader';
import PopUpSearch from '../../components/PopUpSearch';
import { useAdsByLocation } from '../../hooks/queries/useAds';
import { useCityDetail } from '../../hooks/queries/useCities';
import { useFiltersByGenderFilter } from '../../hooks/queries/useFilters';
import { useModelsInfinite } from '../../hooks/queries/useModels';
import { useStore } from '../../stores/index';
import { saveToStorage } from '../../utils/saveToStorage';
import Filters from './components/Filters';
import RenderCards from './components/RenderCards';
import Top from './components/Top';
import * as styles from './styles';

export default function Cities() {
  const { id } = useParams();
  const user = useStore((state) => state.user);

  const [gender, setGender] = useState('WOMAN');
  const [showFilters, setShowFilters] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  // Zustand
  const { filtersSelect, priceRange, clearFilters } = useStore(
    useShallow((state) => ({
      filtersSelect: state.filtersSelect,
      priceRange: state.priceRange,
      clearFilters: state.clearFilters,
    }))
  );

  // City Detail
  const {
    data: city,
    isLoading: isLoadingCity,
    isError: isErrorCity,
    error: cityError,
  } = useCityDetail(id);

  // Models com Infinite Scroll
  const {
    data: modelsData = [],
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingModels,
    isError: isErrorModels,
    error: modelsError,
  } = useModelsInfinite(id, gender, filtersSelect, priceRange);

  // Filters
  const {
    data: filters,
    isError: isErrorFilters,
    error: filtersError,
  } = useFiltersByGenderFilter(gender);

  // Ads
  const {
    data: ads = [],
    isError: isErrorAds,
    error: adsError,
  } = useAdsByLocation('MODEL');

  // Transform all models from pages in array
  const allModels = useMemo(() => {
    return modelsData?.pages?.flatMap((page) => page.models) || [];
  }, [modelsData]);

  // Intercalated models with ads
  const itemsWithAds = useMemo(() => {
    if (allModels.length === 0) {
      return [];
    }

    if (!Array.isArray(ads) || ads.length === 0) {
      // No Ads, return models
      return allModels.map((model) => ({
        type: 'model',
        data: model,
        key: `model-${model.id}`,
      }));
    }

    const items = [];
    let adsIndex = 0;

    allModels.forEach((model, index) => {
      items.push({
        type: 'model',
        data: model,
        key: `model-${model.id}`,
      });

      const isLastModel = index === allModels.length - 1;
      const isMultipleOfSix = (index + 1) % 3 === 0;

      // Se for o último model E tiver menos de 6, adiciona ad
      // OU se completou 6 models (e múltiplos de 6)
      if ((isLastModel && allModels.length < 6) || isMultipleOfSix) {
        const ad = ads[adsIndex % ads.length];
        items.push({
          type: 'ad',
          data: ad,
          key: `ad-${adsIndex}-${ad.id}`,
        });
        adsIndex++;
      }
    });

    return items;
  }, [allModels, ads]);

  // Salva histórico
  useEffect(() => {
    if (city && user?.type === 'CLIENT') {
      const key =
        process.env.REACT_APP_LOCAL_STORAGE_HISTORIC_CITY ||
        'flamehot_historic_city';

      saveToStorage(key, city);
    }
  }, [city, user]);

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      const loadHeight = 1000;
      const scrolled = window.innerHeight + document.documentElement.scrollTop;
      const total = document.documentElement.offsetHeight - loadHeight;

      if (scrolled >= total && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  function handleGenderChange(newGender) {
    setGender(newGender);
  }

  function handleClearFilters() {
    clearFilters();
  }

  function parseChoices(choicesString) {
    if (!choicesString) return [];

    return choicesString
      .split(',')
      .map((choice) => choice.trim())
      .filter(Boolean);
  }

  // Active filters count
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (Array.isArray(filtersSelect)) count += filtersSelect.length;
    if (priceRange.min) count += 1;
    if (priceRange.max) count += 1;
    return count;
  }, [filtersSelect, priceRange]);

  const isError = isErrorCity || isErrorModels || isErrorFilters || isErrorAds;

  const errorMessage =
    cityError?.message ||
    modelsError?.message ||
    filtersError?.message ||
    adsError?.message;

  const topProps = {
    city,
    allModels,
    activeFiltersCount,
    setShowFilters,
  };

  const filterProps = {
    showFilters,
    setShowFilters,
    activeFiltersCount,
    handleClearFilters,
    priceRange,
    filters,
    parseChoices,
  };

  const cardsProps = {
    handleGenderChange,
    gender,
    isLoadingModels,
    activeFiltersCount,
    handleClearFilters,
    itemsWithAds,
    isFetchingNextPage,
    hasNextPage,
    allModels,
  };

  if (isLoadingCity) {
    return <Loader isLoading />;
  }

  if (isError) {
    return <Error isError message={errorMessage} />;
  }

  return (
    <DefaultContainer>
      <Center className="center">
        <styles.Container>
          <Top {...topProps} />
          <Filters {...filterProps} />
          <RenderCards {...cardsProps} />
        </styles.Container>
      </Center>

      <PopUpSearch
        show={showSearch}
        onHideWindow={() => setShowSearch(false)}
      />
    </DefaultContainer>
  );
}
